import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { getExpressDocument } from "../services/expressDocument";
import { ExpressDocType, ExpressDocumentDetail } from "../types/express-document.type";
import DocumentForm from "../components/ExpressDocument/DocumentForm";
import CancelDocumentDialog from "../components/ExpressDocument/CancelDocumentDialog";
import StatusChip from "../components/ExpressDocument/StatusChip";
import {
  EXPRESS_DOC_TYPE_CONFIG,
  ExpressDocTypeConfig,
  documentDetailPath,
  documentPageTitle,
} from "../components/ExpressDocument/constants";

const breadcrumbLinkStyle = { textDecoration: "none", color: "inherit" };

function Breadcrumb({ config, current }: Readonly<{ config: ExpressDocTypeConfig; current: string }>) {
  return (
    <Stack direction="row" alignItems="center" mb={2}>
      <Link to={config.path} style={breadcrumbLinkStyle}>
        <Typography variant="body1" fontWeight={500}>
          {documentPageTitle(config)}
        </Typography>
      </Link>
      <Typography variant="body1" sx={{ mx: 1 }}>
        /
      </Typography>
      <Typography variant="body1" color="primary">
        {current}
      </Typography>
    </Stack>
  );
}

/**
 * /express/<type>/new keys a new document; /express/<type>/:uuid shows a saved one, which an
 * ACTIVE document can switch into editing or cancel from.
 */
export default function ExpressDocumentFormPage({ docType }: Readonly<{ docType: ExpressDocType }>) {
  const config = EXPRESS_DOC_TYPE_CONFIG[docType];
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<ExpressDocumentDetail | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isCancelOpen, setIsCancelOpen] = useState<boolean>(false);
  // Bumped whenever the form must start again from the saved document (a save, a discarded edit).
  const [formVersion, setFormVersion] = useState<number>(0);

  const fetchDocument = useCallback(async () => {
    if (!uuid) return;
    try {
      const {
        data: { data },
      } = await getExpressDocument(uuid);
      // A document opened under another type's path is not one of this list's.
      const isThisType = data?.doc_type === docType;
      setDocument(isThisType ? data : null);
      setNotFound(!isThisType);
    } catch {
      // The interceptor already shows why the document could not be read.
      setNotFound(true);
    }
  }, [uuid, docType]);

  useEffect(() => {
    setDocument(null);
    setNotFound(false);
    setIsEditing(false);
    fetchDocument();
  }, [fetchDocument]);

  const handleBackToList = () => navigate(config.path);
  const handleCreated = (saved: ExpressDocumentDetail | null) =>
    navigate(saved ? documentDetailPath(config, saved.uuid) : config.path);
  const handleUpdated = (saved: ExpressDocumentDetail | null) => {
    if (saved) setDocument(saved);
    else fetchDocument();
    setIsEditing(false);
    setFormVersion((version) => version + 1);
  };
  const handleDiscardEdit = () => {
    setIsEditing(false);
    setFormVersion((version) => version + 1);
  };
  const handleEdit = () => setIsEditing(true);
  const handleOpenCancel = () => setIsCancelOpen(true);
  const handleCloseCancel = () => setIsCancelOpen(false);
  const handleCancelled = () => {
    setIsCancelOpen(false);
    fetchDocument();
    setFormVersion((version) => version + 1);
  };

  if (!uuid) {
    return (
      <Stack sx={{ padding: "8px 0" }}>
        <Breadcrumb config={config} current="บันทึกเอกสารใหม่" />
        <DocumentForm
          config={config}
          document={null}
          readOnly={false}
          viewActions={null}
          onSaved={handleCreated}
          onDiscard={handleBackToList}
        />
      </Stack>
    );
  }

  if (!document) {
    return (
      <Stack sx={{ padding: "8px 0" }}>
        <Breadcrumb config={config} current="…" />
        {notFound ? (
          <Typography color="text.secondary">ไม่พบเอกสารนี้</Typography>
        ) : (
          <Stack alignItems="center" py={8}>
            <CircularProgress />
          </Stack>
        )}
      </Stack>
    );
  }

  const isActive = document.status === "ACTIVE";
  const viewActions = (
    <>
      <Button variant="text" onClick={handleBackToList}>
        กลับไปรายการ
      </Button>
      {isActive && (
        <>
          <Button variant="outlined" color="error" onClick={handleOpenCancel}>
            ยกเลิกเอกสาร
          </Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
            แก้ไข
          </Button>
        </>
      )}
    </>
  );

  return (
    <Stack sx={{ padding: "8px 0" }}>
      <Breadcrumb config={config} current={document.doc_no} />
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          {document.doc_no}
        </Typography>
        <StatusChip status={document.status} />
      </Stack>
      {!isActive && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ยกเลิกแล้ว · เหตุผล: {document.cancel_reason ?? "-"}
        </Alert>
      )}
      <DocumentForm
        key={`${document.uuid}-${formVersion}`}
        config={config}
        document={document}
        readOnly={!isEditing}
        viewActions={viewActions}
        onSaved={handleUpdated}
        onDiscard={handleDiscardEdit}
      />
      <CancelDocumentDialog
        open={isCancelOpen}
        documentUuid={document.uuid}
        docNo={document.doc_no}
        onClose={handleCloseCancel}
        onCancelled={handleCancelled}
      />
    </Stack>
  );
}
